import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@companion-professional/webutils';
import { LoaderSpinner } from '../LoaderSpinner';

export const buttonVariants = cva(
  [
    'inline-flex',
    'items-center',
    'justify-center',
    'gap-2',
    'whitespace-nowrap',
    'rounded-full',
    'font-sans',
    'font-semibold',
    'leading-none',
    'ring-offset-background',
    'transition-colors',
    'focus-visible:outline-none',
    'focus-visible:ring-2',
    'focus-visible:ring-ring',
    'focus-visible:ring-offset-2',
    'disabled:pointer-events-none',
    'disabled:opacity-50'
  ],
  {
    /** Variants are different types of buttons that come fully styled */
    variants: {
      variant: {
        primary: ['bg-primary', 'text-on-primary', 'hover:bg-primary/90'],
        // destructive is for confirming a destructive action (e.g. the "Archive" button inside a confirmation dialog).
        destructive: ['bg-destructive', 'text-on-destructive', 'hover:bg-destructive/90'],
        // destructiveOutline is for entry points to a destructive action that sit alongside other actions (e.g. an
        // "Archive" button that opens a confirmation dialog).  It flags the action as dangerous without making it the
        // most prominent thing on the page.
        destructiveOutline: [
          'border',
          'border-destructive/40',
          'bg-widget',
          'text-destructive',
          'hover:border-destructive',
          'hover:bg-destructive/5'
        ],
        outline: [
          'border',
          'border-primary/40',
          'bg-widget',
          'text-primary',
          'hover:border-primary',
          'hover:bg-accent/40'
        ],
        secondary: ['bg-secondary', 'text-on-secondary', 'hover:bg-secondary/85'],
        ghost: ['text-primary', 'hover:bg-accent/50', 'focus-visible:ring-offset-0'],
        link: ['text-link', 'underline-offset-4', 'hover:underline']
      },
      size: {
        thin: 'h-7 px-3 py-1 text-sm',
        small: 'h-9 px-4 py-1 text-md',
        medium: 'h-11 px-6 py-2 text-base',
        large: 'h-12 px-8 py-3 text-lg',
        icon: 'h-9 w-9',
        // smallThenIcon starts out as a small button, but switches to an icon button on smaller screens.  Note: you
        // will still need to handle hiding text inside the button:
        // <span className="hidden lg:block"> Archive Checkup</span>
        smallThenIcon: 'h-9 w-9 lg:h-9 lg:w-auto lg:px-4 lg:py-1 text-md'
      }
    },
    defaultVariants: {
      variant: 'primary',
      size: 'medium'
    }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;

  // loading shows a spinner before the children and disables the button (e.g. while a form submits).  Pass the
  // in-progress label as children, e.g. {isSaving ? 'Saving...' : 'Save'}.  Ignored when asChild is set.
  loading?: boolean;
}

// Button is a styled button component that can be used to create a variety of button styles.  It takes all the props
// that a normal <button /> would take, but the type is defaulted to 'button'.  The additional props are:
// * variant: The variant of the button (primary, destructive, destructiveOutline, outline, secondary, ghost, link).
// * size: The size of the button (thin, small, medium, large, icon).
// * loading: Shows a spinner and disables the button.
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading = false, type = 'button', disabled, children, ...props },
    ref
  ) => {
    if (asChild) {
      return (
        <Slot className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
          {children}
        </Slot>
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        type={type}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? <LoaderSpinner size="small" tone="current" /> : null}
        {children}
      </button>
    );
  }
);
Button.displayName = 'Button';

export { Button };
